
import { useState, useEffect } from 'react';

export interface CryptoAsset {
  id: string;
  symbol: string;
  name: string;
  current_price: number;
  price_change_percentage_24h: number;
  market_cap: number;
  total_volume: number;
  image: string;
  // Keep legacy properties for backward compatibility
  price: number;
  change24h: number;
  marketCap: number;
  volume24h: number;
}

export interface Portfolio {
  id: string;
  symbol: string;
  name: string;
  amount: number;
  avgPrice: number;
  currentPrice: number;
  totalValue: number;
  profit: number;
  profitPercent: number;
}

export interface Transaction {
  id: string;
  type: 'buy' | 'sell' | 'deposit' | 'withdrawal' | 'transfer' | 'convert';
  asset: string;
  amount: number;
  price?: number;
  total?: number;
  status: 'completed' | 'pending' | 'failed';
  date: string;
  txHash?: string;
  fromAsset?: string;
  toAsset?: string;
  toAmount?: number;
  address?: string;
}

export interface WalletBalance {
  asset: string;
  symbol: string;
  balance: number;
  value: number;
  change: number;
}

export function useCrypto() {
  const [cryptoData, setCryptoData] = useState<CryptoAsset[]>([]);
  const [portfolio, setPortfolio] = useState<Portfolio[]>([]);
  const [transactions, setTransactions] = useState<Transaction[]>([]);
  const [walletBalances, setWalletBalances] = useState<WalletBalance[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [loading, setLoading] = useState(true);

  // Mock crypto data with both naming conventions
  const mockCryptoData: CryptoAsset[] = [
    {
      id: 'bitcoin',
      symbol: 'btc',
      name: 'Bitcoin',
      current_price: 43250.50,
      price_change_percentage_24h: 2.45,
      market_cap: 847000000000,
      total_volume: 28500000000,
      image: 'https://readdy.ai/api/search-image?query=Bitcoin%20cryptocurrency%20golden%20coin%20with%20B%20symbol%20on%20clean%20white%20background%2C%20professional%20product%20photography%2C%20high%20quality%2C%20detailed&width=64&height=64&seq=btc001&orientation=squarish',
      price: 43250.50,
      change24h: 2.45,
      marketCap: 847000000000,
      volume24h: 28500000000
    },
    {
      id: 'ethereum',
      symbol: 'eth',
      name: 'Ethereum',
      current_price: 2650.75,
      price_change_percentage_24h: -1.23,
      market_cap: 318000000000,
      total_volume: 15200000000,
      image: 'https://readdy.ai/api/search-image?query=Ethereum%20cryptocurrency%20silver%20coin%20with%20ETH%20diamond%20symbol%20on%20clean%20white%20background%2C%20professional%20product%20photography%2C%20high%20quality%2C%20detailed&width=64&height=64&seq=eth001&orientation=squarish',
      price: 2650.75,
      change24h: -1.23,
      marketCap: 318000000000,
      volume24h: 15200000000
    },
    {
      id: 'cardano',
      symbol: 'ada',
      name: 'Cardano',
      current_price: 0.485,
      price_change_percentage_24h: 5.67,
      market_cap: 17200000000,
      total_volume: 890000000,
      image: 'https://readdy.ai/api/search-image?query=Cardano%20ADA%20cryptocurrency%20blue%20coin%20with%20circular%20symbol%20on%20clean%20white%20background%2C%20professional%20product%20photography%2C%20high%20quality%2C%20detailed&width=64&height=64&seq=ada001&orientation=squarish',
      price: 0.485,
      change24h: 5.67,
      marketCap: 17200000000,
      volume24h: 890000000
    },
    {
      id: 'solana',
      symbol: 'sol',
      name: 'Solana',
      current_price: 98.45,
      price_change_percentage_24h: 8.92,
      market_cap: 42800000000,
      total_volume: 2100000000,
      image: 'https://readdy.ai/api/search-image?query=Solana%20SOL%20cryptocurrency%20purple%20gradient%20coin%20with%20modern%20S%20symbol%20on%20clean%20white%20background%2C%20professional%20product%20photography%2C%20high%20quality%2C%20detailed&width=64&height=64&seq=sol001&orientation=squarish',
      price: 98.45,
      change24h: 8.92,
      marketCap: 42800000000,
      volume24h: 2100000000
    },
    {
      id: 'polkadot',
      symbol: 'dot',
      name: 'Polkadot',
      current_price: 7.23,
      price_change_percentage_24h: -3.45,
      market_cap: 9500000000,
      total_volume: 420000000,
      image: 'https://readdy.ai/api/search-image?query=Polkadot%20DOT%20cryptocurrency%20pink%20coin%20with%20circular%20dots%20pattern%20symbol%20on%20clean%20white%20background%2C%20professional%20product%20photography%2C%20high%20quality%2C%20detailed&width=64&height=64&seq=dot001&orientation=squarish',
      price: 7.23,
      change24h: -3.45,
      marketCap: 9500000000,
      volume24h: 420000000
    },
    {
      id: 'binancecoin',
      symbol: 'bnb',
      name: 'BNB',
      current_price: 315.80,
      price_change_percentage_24h: 1.85,
      market_cap: 47200000000,
      total_volume: 1800000000,
      image: 'https://readdy.ai/api/search-image?query=Binance%20BNB%20cryptocurrency%20yellow%20coin%20with%20diamond%20symbol%20on%20clean%20white%20background%2C%20professional%20product%20photography%2C%20high%20quality%2C%20detailed&width=64&height=64&seq=bnb001&orientation=squarish',
      price: 315.80,
      change24h: 1.85,
      marketCap: 47200000000,
      volume24h: 1800000000
    },
    {
      id: 'ripple',
      symbol: 'xrp',
      name: 'XRP',
      current_price: 0.62,
      price_change_percentage_24h: -2.15,
      market_cap: 33500000000,
      total_volume: 1200000000,
      image: 'https://readdy.ai/api/search-image?query=Ripple%20XRP%20cryptocurrency%20silver%20coin%20with%20X%20symbol%20on%20clean%20white%20background%2C%20professional%20product%20photography%2C%20high%20quality%2C%20detailed&width=64&height=64&seq=xrp001&orientation=squarish',
      price: 0.62,
      change24h: -2.15,
      marketCap: 33500000000,
      volume24h: 1200000000
    },
    {
      id: 'chainlink',
      symbol: 'link',
      name: 'Chainlink',
      current_price: 14.85,
      price_change_percentage_24h: 4.32,
      market_cap: 8700000000,
      total_volume: 650000000,
      image: 'https://readdy.ai/api/search-image?query=Chainlink%20LINK%20cryptocurrency%20blue%20coin%20with%20chain%20link%20symbol%20on%20clean%20white%20background%2C%20professional%20product%20photography%2C%20high%20quality%2C%20detailed&width=64&height=64&seq=link001&orientation=squarish',
      price: 14.85,
      change24h: 4.32,
      marketCap: 8700000000,
      volume24h: 650000000
    },
    {
      id: 'litecoin',
      symbol: 'ltc',
      name: 'Litecoin',
      current_price: 72.45,
      price_change_percentage_24h: -0.85,
      market_cap: 5400000000,
      total_volume: 420000000,
      image: 'https://readdy.ai/api/search-image?query=Litecoin%20LTC%20cryptocurrency%20silver%20coin%20with%20L%20symbol%20on%20clean%20white%20background%2C%20professional%20product%20photography%2C%20high%20quality%2C%20detailed&width=64&height=64&seq=ltc001&orientation=squarish',
      price: 72.45,
      change24h: -0.85,
      marketCap: 5400000000,
      volume24h: 420000000
    },
    {
      id: 'polygon',
      symbol: 'matic',
      name: 'Polygon',
      current_price: 0.89,
      price_change_percentage_24h: 6.75,
      market_cap: 8200000000,
      total_volume: 380000000,
      image: 'https://readdy.ai/api/search-image?query=Polygon%20MATIC%20cryptocurrency%20purple%20coin%20with%20geometric%20symbol%20on%20clean%20white%20background%2C%20professional%20product%20photography%2C%20high%20quality%2C%20detailed&width=64&height=64&seq=matic001&orientation=squarish',
      price: 0.89,
      change24h: 6.75,
      marketCap: 8200000000,
      volume24h: 380000000
    }
  ];

  // Load data from localStorage
  const loadFromStorage = () => {
    try {
      const storedPortfolio = localStorage.getItem('crypto_portfolio');
      const storedTransactions = localStorage.getItem('crypto_transactions');
      const storedWalletBalances = localStorage.getItem('crypto_wallet_balances');

      if (storedPortfolio) {
        setPortfolio(JSON.parse(storedPortfolio));
      }

      if (storedTransactions) {
        setTransactions(JSON.parse(storedTransactions));
      }

      if (storedWalletBalances) {
        setWalletBalances(JSON.parse(storedWalletBalances));
      } else {
        // Initialize with default balances
        const defaultBalances: WalletBalance[] = [
          { asset: 'bitcoin', symbol: 'BTC', balance: 2.45678, value: 106234.56, change: 2.34 },
          { asset: 'ethereum', symbol: 'ETH', balance: 15.8923, value: 38456.78, change: -1.23 },
          { asset: 'binancecoin', symbol: 'BNB', balance: 45.234, value: 12345.67, change: 5.67 },
          { asset: 'cardano', symbol: 'ADA', balance: 1250.45, value: 456.78, change: -0.89 },
          { asset: 'solana', symbol: 'SOL', balance: 8.567, value: 1234.56, change: 3.45 },
        ];
        setWalletBalances(defaultBalances);
        localStorage.setItem('crypto_wallet_balances', JSON.stringify(defaultBalances));
      }
    } catch (error) {
      console.error('Error loading from localStorage:', error);
    }
  };

  // Save data to localStorage
  const saveToStorage = (key: string, data: any) => {
    try {
      localStorage.setItem(key, JSON.stringify(data));
    } catch (error) {
      console.error('Error saving to localStorage:', error);
    }
  };

  useEffect(() => {
    const fetchData = async () => {
      setIsLoading(true);
      setLoading(true);
      
      // Load stored data
      loadFromStorage();
      
      // Simulate API call
      await new Promise(resolve => setTimeout(resolve, 1000));
      setCryptoData(mockCryptoData);
      setIsLoading(false);
      setLoading(false);
    };

    fetchData();
    
    // Update prices every 30 seconds
    const interval = setInterval(() => {
      setCryptoData(prev => prev.map(crypto => {
        const newPrice = crypto.current_price * (1 + (Math.random() - 0.5) * 0.02);
        const newChange = crypto.price_change_percentage_24h + (Math.random() - 0.5) * 2;
        return {
          ...crypto,
          current_price: newPrice,
          price_change_percentage_24h: newChange,
          price: newPrice,
          change24h: newChange
        };
      }));

      // Update wallet balances with new prices
      setWalletBalances(prev => {
        const updated = prev.map(balance => {
          const crypto = mockCryptoData.find(c => c.id === balance.asset);
          if (crypto) {
            const newValue = balance.balance * crypto.current_price;
            return { ...balance, value: newValue };
          }
          return balance;
        });
        saveToStorage('crypto_wallet_balances', updated);
        return updated;
      });
    }, 30000);

    return () => clearInterval(interval);
  }, []);

  const buyCrypto = (symbol: string, amount: number, price: number) => {
    const crypto = cryptoData.find(c => c.symbol.toLowerCase() === symbol.toLowerCase());
    if (!crypto) return false;

    const total = amount * price;
    
    // Create transaction
    const transaction: Transaction = {
      id: Date.now().toString(),
      type: 'buy',
      asset: symbol.toUpperCase(),
      amount,
      price,
      total,
      status: 'completed',
      date: new Date().toISOString(),
      txHash: `0x${Math.random().toString(16).substr(2, 8)}`
    };

    // Update portfolio
    setPortfolio(prev => {
      const existingHolding = prev.find(p => p.symbol.toLowerCase() === symbol.toLowerCase());
      let newPortfolio;
      
      if (existingHolding) {
        const newAmount = existingHolding.amount + amount;
        const newAvgPrice = ((existingHolding.avgPrice * existingHolding.amount) + (price * amount)) / newAmount;
        
        newPortfolio = prev.map(p => 
          p.symbol.toLowerCase() === symbol.toLowerCase()
            ? {
                ...p,
                amount: newAmount,
                avgPrice: newAvgPrice,
                currentPrice: price,
                totalValue: newAmount * price,
                profit: (price - newAvgPrice) * newAmount,
                profitPercent: ((price - newAvgPrice) / newAvgPrice) * 100
              }
            : p
        );
      } else {
        const newHolding: Portfolio = {
          id: `${symbol.toLowerCase()}-holding`,
          symbol: symbol.toUpperCase(),
          name: crypto.name,
          amount,
          avgPrice: price,
          currentPrice: price,
          totalValue: amount * price,
          profit: 0,
          profitPercent: 0
        };
        newPortfolio = [...prev, newHolding];
      }
      
      saveToStorage('crypto_portfolio', newPortfolio);
      return newPortfolio;
    });

    // Update wallet balance
    setWalletBalances(prev => {
      const newBalances = prev.map(balance => {
        if (balance.asset === crypto.id) {
          return {
            ...balance,
            balance: balance.balance + amount,
            value: (balance.balance + amount) * price
          };
        }
        return balance;
      });
      
      // Add new asset if not exists
      if (!prev.find(b => b.asset === crypto.id)) {
        newBalances.push({
          asset: crypto.id,
          symbol: symbol.toUpperCase(),
          balance: amount,
          value: amount * price,
          change: 0
        });
      }
      
      saveToStorage('crypto_wallet_balances', newBalances);
      return newBalances;
    });

    // Add transaction
    setTransactions(prev => {
      const newTransactions = [transaction, ...prev];
      saveToStorage('crypto_transactions', newTransactions);
      return newTransactions;
    });

    return true;
  };

  const sellCrypto = (symbol: string, amount: number, price: number) => {
    const balance = walletBalances.find(b => b.symbol.toLowerCase() === symbol.toLowerCase());
    if (!balance || balance.balance < amount) return false;

    const total = amount * price;
    
    // Create transaction
    const transaction: Transaction = {
      id: Date.now().toString(),
      type: 'sell',
      asset: symbol.toUpperCase(),
      amount,
      price,
      total,
      status: 'completed',
      date: new Date().toISOString(),
      txHash: `0x${Math.random().toString(16).substr(2, 8)}`
    };

    // Update portfolio
    setPortfolio(prev => {
      const newPortfolio = prev.map(p => {
        if (p.symbol.toLowerCase() === symbol.toLowerCase() && p.amount >= amount) {
          const newAmount = p.amount - amount;
          return newAmount > 0 
            ? { 
                ...p, 
                amount: newAmount, 
                totalValue: newAmount * price,
                profit: (price - p.avgPrice) * newAmount,
                profitPercent: ((price - p.avgPrice) / p.avgPrice) * 100
              }
            : null;
        }
        return p;
      }).filter(Boolean) as Portfolio[];
      
      saveToStorage('crypto_portfolio', newPortfolio);
      return newPortfolio;
    });

    // Update wallet balance
    setWalletBalances(prev => {
      const newBalances = prev.map(balance => {
        if (balance.symbol.toLowerCase() === symbol.toLowerCase()) {
          return {
            ...balance,
            balance: balance.balance - amount,
            value: (balance.balance - amount) * price
          };
        }
        return balance;
      });
      
      saveToStorage('crypto_wallet_balances', newBalances);
      return newBalances;
    });

    // Add transaction
    setTransactions(prev => {
      const newTransactions = [transaction, ...prev];
      saveToStorage('crypto_transactions', newTransactions);
      return newTransactions;
    });

    return true;
  };

  const convertCrypto = (fromSymbol: string, toSymbol: string, fromAmount: number, toAmount: number, rate: number) => {
    const fromBalance = walletBalances.find(b => b.symbol.toLowerCase() === fromSymbol.toLowerCase());
    if (!fromBalance || fromBalance.balance < fromAmount) return false;

    const toCrypto = cryptoData.find(c => c.symbol.toLowerCase() === toSymbol.toLowerCase());
    if (!toCrypto) return false;

    // Create transaction
    const transaction: Transaction = {
      id: Date.now().toString(),
      type: 'convert',
      asset: fromSymbol.toUpperCase(),
      amount: fromAmount,
      status: 'completed',
      date: new Date().toISOString(),
      txHash: `0x${Math.random().toString(16).substr(2, 8)}`,
      fromAsset: fromSymbol.toUpperCase(),
      toAsset: toSymbol.toUpperCase(),
      toAmount
    };

    // Update wallet balances
    setWalletBalances(prev => {
      const newBalances = prev.map(balance => {
        if (balance.symbol.toLowerCase() === fromSymbol.toLowerCase()) {
          return {
            ...balance,
            balance: balance.balance - fromAmount,
            value: (balance.balance - fromAmount) * (balance.value / balance.balance)
          };
        }
        if (balance.symbol.toLowerCase() === toSymbol.toLowerCase()) {
          return {
            ...balance,
            balance: balance.balance + toAmount,
            value: (balance.balance + toAmount) * toCrypto.current_price
          };
        }
        return balance;
      });

      // Add new asset if not exists
      if (!prev.find(b => b.symbol.toLowerCase() === toSymbol.toLowerCase())) {
        newBalances.push({
          asset: toCrypto.id,
          symbol: toSymbol.toUpperCase(),
          balance: toAmount,
          value: toAmount * toCrypto.current_price,
          change: 0
        });
      }
      
      saveToStorage('crypto_wallet_balances', newBalances);
      return newBalances;
    });

    // Add transaction
    setTransactions(prev => {
      const newTransactions = [transaction, ...prev];
      saveToStorage('crypto_transactions', newTransactions);
      return newTransactions;
    });

    return true;
  };

  const depositCrypto = (symbol: string, amount: number, address: string) => {
    const crypto = cryptoData.find(c => c.symbol.toLowerCase() === symbol.toLowerCase());
    if (!crypto) return false;

    // Create transaction
    const transaction: Transaction = {
      id: Date.now().toString(),
      type: 'deposit',
      asset: symbol.toUpperCase(),
      amount,
      status: 'pending',
      date: new Date().toISOString(),
      txHash: `0x${Math.random().toString(16).substr(2, 8)}`,
      address
    };

    // Update wallet balance after delay (simulate network confirmation)
    setTimeout(() => {
      setWalletBalances(prev => {
        const newBalances = prev.map(balance => {
          if (balance.symbol.toLowerCase() === symbol.toLowerCase()) {
            return {
              ...balance,
              balance: balance.balance + amount,
              value: (balance.balance + amount) * crypto.current_price
            };
          }
          return balance;
        });

        // Add new asset if not exists
        if (!prev.find(b => b.symbol.toLowerCase() === symbol.toLowerCase())) {
          newBalances.push({
            asset: crypto.id,
            symbol: symbol.toUpperCase(),
            balance: amount,
            value: amount * crypto.current_price,
            change: 0
          });
        }
        
        saveToStorage('crypto_wallet_balances', newBalances);
        return newBalances;
      });

      // Update transaction status
      setTransactions(prev => {
        const newTransactions = prev.map(tx => 
          tx.id === transaction.id ? { ...tx, status: 'completed' as const } : tx
        );
        saveToStorage('crypto_transactions', newTransactions);
        return newTransactions;
      });
    }, 3000);

    // Add transaction
    setTransactions(prev => {
      const newTransactions = [transaction, ...prev];
      saveToStorage('crypto_transactions', newTransactions);
      return newTransactions;
    });

    return true;
  };

  const withdrawCrypto = (symbol: string, amount: number, address: string) => {
    const balance = walletBalances.find(b => b.symbol.toLowerCase() === symbol.toLowerCase());
    if (!balance || balance.balance < amount) return false;

    // Create transaction
    const transaction: Transaction = {
      id: Date.now().toString(),
      type: 'withdrawal',
      asset: symbol.toUpperCase(),
      amount,
      status: 'pending',
      date: new Date().toISOString(),
      txHash: `0x${Math.random().toString(16).substr(2, 8)}`,
      address
    };

    // Update wallet balance immediately
    setWalletBalances(prev => {
      const newBalances = prev.map(balance => {
        if (balance.symbol.toLowerCase() === symbol.toLowerCase()) {
          return {
            ...balance,
            balance: balance.balance - amount,
            value: (balance.balance - amount) * (balance.value / balance.balance)
          };
        }
        return balance;
      });
      
      saveToStorage('crypto_wallet_balances', newBalances);
      return newBalances;
    });

    // Update transaction status after delay
    setTimeout(() => {
      setTransactions(prev => {
        const newTransactions = prev.map(tx => 
          tx.id === transaction.id ? { ...tx, status: 'completed' as const } : tx
        );
        saveToStorage('crypto_transactions', newTransactions);
        return newTransactions;
      });
    }, 5000);

    // Add transaction
    setTransactions(prev => {
      const newTransactions = [transaction, ...prev];
      saveToStorage('crypto_transactions', newTransactions);
      return newTransactions;
    });

    return true;
  };

  const transferCrypto = (symbol: string, amount: number, address: string) => {
    const balance = walletBalances.find(b => b.symbol.toLowerCase() === symbol.toLowerCase());
    if (!balance || balance.balance < amount) return false;

    // Create transaction
    const transaction: Transaction = {
      id: Date.now().toString(),
      type: 'transfer',
      asset: symbol.toUpperCase(),
      amount,
      status: 'pending',
      date: new Date().toISOString(),
      txHash: `0x${Math.random().toString(16).substr(2, 8)}`,
      address
    };

    // Update wallet balance immediately
    setWalletBalances(prev => {
      const newBalances = prev.map(balance => {
        if (balance.symbol.toLowerCase() === symbol.toLowerCase()) {
          return {
            ...balance,
            balance: balance.balance - amount,
            value: (balance.balance - amount) * (balance.value / balance.balance)
          };
        }
        return balance;
      });
      
      saveToStorage('crypto_wallet_balances', newBalances);
      return newBalances;
    });

    // Update transaction status after delay
    setTimeout(() => {
      setTransactions(prev => {
        const newTransactions = prev.map(tx => 
          tx.id === transaction.id ? { ...tx, status: 'completed' as const } : tx
        );
        saveToStorage('crypto_transactions', newTransactions);
        return newTransactions;
      });
    }, 4000);

    // Add transaction
    setTransactions(prev => {
      const newTransactions = [transaction, ...prev];
      saveToStorage('crypto_transactions', newTransactions);
      return newTransactions;
    });

    return true;
  };

  return {
    cryptoData,
    portfolio,
    transactions,
    walletBalances,
    isLoading,
    loading,
    buyCrypto,
    sellCrypto,
    convertCrypto,
    depositCrypto,
    withdrawCrypto,
    transferCrypto
  };
}